import { Link, NavLink, Outlet, useLocation } from 'react-router';
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuBadge,
  SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton,
  SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useNumber, useT, useTn } from '@/lib/i18n';
import { useQcHeldCount } from '@/lib/qc';
import { navGroups, type NavBadge } from './nav';
import { GlobalSearch } from './global-search';
import { ThemeSwitch } from './theme-switch';
import { NotificationsMenu } from './notifications-menu';
import { EnvironmentBadge } from './environment-badge';
import { UserChip } from './user-chip';

function isActive(pathname: string, href: string) {
  return href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`);
}

export function AppLayout() {
  const { pathname } = useLocation();
  const t = useT();
  const tn = useTn();
  const number = useNumber();

  /**
   * The QC hold count (Workstream V3) — a REAL number, from
   * `GET /api/results/qc-queue`, on the same react-query key the QC review
   * screen reads, so the two can never disagree.
   *
   * This is the one thing the bell was always meant to carry, and the reason
   * `lib/notifications.ts` has an empty sample feed rather than a plausible one:
   * holds accumulate silently, and an operator who never opens the screen has no
   * way to learn that anything is being withheld. It stays a count on a nav item
   * — not a feed, not a notification, and nothing is invented at zero.
   */
  const qcHeld = useQcHeldCount();
  const counts: Record<NavBadge, number> = { qcQueue: qcHeld };
  /** The sentence a screen reader gets. Plural-aware, because Arabic has six. */
  const countLabel: Record<NavBadge, (n: number) => string> = {
    qcQueue: (n) => tn('nav.qcReview.held', n),
  };

  const mainGroups = navGroups.filter((g) => !g.footer);
  const footerGroups = navGroups.filter((g) => g.footer);

  const renderItems = (group: (typeof navGroups)[number]) => (
    <SidebarMenu>
      {group.items.map((item) => {
        const active = isActive(pathname, item.href);
        // QUIET AT ZERO. A "0" badge is a permanent ornament that trains an
        // operator to stop reading the thing that matters when it is not zero.
        const count = item.badge ? counts[item.badge] : 0;
        const badged = Boolean(item.badge) && count > 0;

        return (
          <SidebarMenuItem key={item.href}>
            <SidebarMenuButton asChild isActive={active && !item.items} tooltip={t(item.title)}>
              <NavLink to={item.href}>
                {item.icon && <item.icon />}
                <span>{t(item.title)}</span>
                {/* Inside the link, so the count is part of its ACCESSIBLE NAME
                    rather than a coloured pill beside it: "QC review, 3 results
                    held for review" is what a screen reader announces, and the
                    numeral below is the same fact drawn. */}
                {badged && item.badge && (
                  <span className="sr-only">{countLabel[item.badge](count)}</span>
                )}
              </NavLink>
            </SidebarMenuButton>

            {badged && (
              // `aria-hidden`: the sr-only sentence above already says it, and
              // announcing a bare "3" after the link name would be worse than
              // silence.
              //
              // NO COLOUR OVERRIDE. The kit's own classes already switch this to
              // `--sidebar-accent-foreground` when the row is active, and pairing
              // any other family against `--sidebar-accent` is the rule that
              // measured 1.00 contrast in twelve of eighteen preset×mode
              // combinations (CLAUDE.md, `rtl.test.tsx`). The count is not
              // conveyed by colour anyway — it is in the link's accessible name.
              <SidebarMenuBadge aria-hidden="true" className="font-semibold">
                {number(count)}
              </SidebarMenuBadge>
            )}

            {/* Questions is the one nested group in the walkthrough, and it
                COLLAPSES (`plan-beta` E). Two permanent sub-items under a nav
                entry that is itself a link is three ways to reach the same
                screen; showing them only when the section is open keeps the
                sidebar the length of the nav rather than the length of the nav
                plus its children. */}
            {item.items && active && (
              <SidebarMenuSub>
                {item.items.map((sub) => (
                  <SidebarMenuSubItem key={sub.href}>
                    <SidebarMenuSubButton asChild isActive={pathname === sub.href}>
                      <NavLink to={sub.href}>{t(sub.title)}</NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <SidebarProvider>
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                {/* Exempt from the collapsed `size-8!` the menu buttons take:
                    the mark inside is 36px, and forcing its button to 32px is
                    what clipped it against the rail. */}
                <SidebarMenuButton
                  size="lg"
                  asChild
                  className="group-data-[collapsible=icon]:size-9! group-data-[collapsible=icon]:p-0!"
                >
                  <Link to="/">
                    {/* The walkthrough's mark: 38px, 10px radius, a deep-to-
                        primary gradient rather than a flat fill. */}
                    <div
                      className="flex aspect-square size-9 items-center justify-center rounded-[10px] text-base font-extrabold text-white"
                      style={{ background: 'linear-gradient(135deg,#153a8a,#2563eb)' }}
                    >
                      N
                    </div>
                    <div className="grid flex-1 text-start leading-tight">
                      <span className="truncate text-sm font-semibold">{t('chrome.brand')}</span>
                      {/* GOLD, uppercase, tracked — the walkthrough's
                          `.side-brand small`. It read as muted grey before. */}
                      <span className="truncate text-[10.5px] font-bold tracking-[0.08em] uppercase brand-gold">
                        {t('chrome.brandSub')}
                      </span>
                    </div>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarContent>
            {mainGroups.map((group) => (
              <SidebarGroup key={group.title}>
                <SidebarGroupLabel className="text-[10.5px] font-bold tracking-[0.09em] uppercase">
                  {group.title && t(group.title)}
                </SidebarGroupLabel>
                <SidebarGroupContent>{renderItems(group)}</SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>

          {/* Notifications / Support / Settings sit at the bottom, after a
              spacer, exactly as the walkthrough arranges them. */}
          <SidebarFooter>
            {footerGroups.map((group, i) => (
              <SidebarGroup key={i} className="p-0">
                <SidebarGroupContent>{renderItems(group)}</SidebarGroupContent>
              </SidebarGroup>
            ))}
            <p className="text-muted-foreground px-2 pb-1 text-[10px] leading-relaxed group-data-[collapsible=icon]:hidden">
              {t('chrome.copyright')}
              <br />
              {t('chrome.residency')}
            </p>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ms-1" />
            <Separator orientation="vertical" className="me-2 h-4" />

            {/* X2: this input was `disabled` while advertising "Search
                questions, candidates, modules…". It searches those three now,
                and only those three — see `global-search.tsx`. */}
            <GlobalSearch />

            <div className="ms-auto flex items-center gap-2">
              {/* Renders nothing in production — see environment-badge.tsx. */}
              <EnvironmentBadge />
              <NotificationsMenu />
              <ThemeSwitch />
              <UserChip />
            </div>
          </header>

          <main className="flex-1 p-4 md:p-6">
            {/* No crumb passed down any more: `<Breadcrumbs>` derives the trail
                from the route, so a page cannot disagree with the nav about
                where it sits. */}
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
